import { Phone } from 'lucide-react'
import type { FlatInvitation } from '../../../stores/invitationStore.js'
import { Countdown } from '../../ui/Countdown.js'
import { formatWeddingDate } from './formatters.js'
import { WeddingCalendar } from './WeddingCalendar.js'
import common from './common.module.css'

interface CeremonyBlockProps {
  invitation: FlatInvitation
  layout?: 'list' | 'card-pair'
  css: Record<string, string>
}

type CalendarStyle = 'none' | 'calendar' | 'countdown' | 'minimal'

function DateDisplay({ dateStr, calendarStyle, dateFormatted, css }: {
  dateStr?: string
  calendarStyle: CalendarStyle
  dateFormatted: string | null
  css: Record<string, string>
}) {
  if (calendarStyle === 'none') return null

  let weddingDate: Date | null = null
  if (dateStr) {
    const d = new Date(dateStr)
    if (!isNaN(d.getTime())) weddingDate = d
  }

  return (
    <>
      {dateFormatted && <p className={css.dateText}>{dateFormatted}</p>}
      {calendarStyle === 'calendar' && weddingDate && (
        <WeddingCalendar date={weddingDate} />
      )}
      {calendarStyle === 'countdown' && weddingDate && (
        <Countdown targetDate={weddingDate} variant="elegant" size="sm" />
      )}
    </>
  )
}

export function CeremonyBlock({ invitation, layout = 'list', css }: CeremonyBlockProps) {
  const dateFormatted = formatWeddingDate(invitation.wedding?.date)
  const venue = invitation.wedding?.venue
  const calendarStyle = (invitation.settings?.calendarStyle ?? 'calendar') as CalendarStyle

  if (layout === 'card-pair') {
    return <CeremonyCardPair
      dateFormatted={dateFormatted}
      dateStr={invitation.wedding?.date}
      venue={venue}
      calendarStyle={calendarStyle}
      css={css}
    />
  }

  // layout === 'list' (classic)
  return (
    <div className={`${common.section} ${css.ceremonySection ?? ''}`}>
      <span className={common.sectionTitle}>예식 안내</span>
      <div className={common.divider} />
      <DateDisplay
        dateStr={invitation.wedding?.date}
        calendarStyle={calendarStyle}
        dateFormatted={dateFormatted}
        css={css}
      />
      {venue?.name && <p className={css.venueText}>{venue.name}{venue.hall ? ` ${venue.hall}` : ''}</p>}
      {venue?.address && <p className={css.venueText}>{venue.address}</p>}
      {venue?.tel && (
        <p className={css.venueTel}>
          <Phone size={10} style={{ display: 'inline', verticalAlign: 'middle' }} /> {venue.tel}
        </p>
      )}
      {!dateFormatted && !venue?.name && (
        <p className={css.venueText} style={{ fontStyle: 'italic' }}>예식 정보를 입력해주세요</p>
      )}
    </div>
  )
}

/** card-pair 레이아웃: 날짜/장소를 양쪽 카드로 표시 */
function CeremonyCardPair({
  dateFormatted, dateStr, venue, calendarStyle, css,
}: {
  dateFormatted: string | null
  dateStr?: string
  venue?: { name?: string; hall?: string; address?: string; tel?: string }
  calendarStyle: CalendarStyle
  css: Record<string, string>
}) {
  let day = ''
  let dayOfWeek = ''
  let time = ''
  if (dateStr) {
    try {
      const d = new Date(dateStr)
      const days = ['일', '월', '화', '수', '목', '금', '토']
      day = `${d.getMonth() + 1}/${d.getDate()}`
      dayOfWeek = `${days[d.getDay()]}요일`
      const h = d.getHours()
      const min = d.getMinutes()
      const ampm = h < 12 ? '오전' : '오후'
      const h12 = h % 12 || 12
      time = `${ampm} ${h12}:${min.toString().padStart(2, '0')}`
    } catch { /* ignore */ }
  }

  const showDateCards = calendarStyle !== 'none' && calendarStyle !== 'calendar' && calendarStyle !== 'countdown'

  return (
    <div className={common.section}>
      <span className={common.sectionTitle}>예식 안내</span>
      <div className={common.divider} />
      <DateDisplay
        dateStr={dateStr}
        calendarStyle={calendarStyle}
        dateFormatted={null}
        css={css}
      />
      <div className={css.ceremonyGrid}>
        {showDateCards && (
          <div className={css.ceremonyCard}>
            <span className={css.ceremonyCardLabel}>날짜</span>
            <span className={css.ceremonyCardPrimary}>{day || '–'}</span>
            <span className={css.ceremonyCardSecondary}>{dayOfWeek}</span>
            <span className={css.ceremonyCardSecondary}>{time}</span>
          </div>
        )}
        <div className={css.ceremonyCard}>
          <span className={css.ceremonyCardLabel}>장소</span>
          <span className={css.ceremonyCardPrimary}>{venue?.name || '–'}</span>
          {venue?.hall && <span className={css.ceremonyCardSecondary}>{venue.hall}</span>}
          {venue?.address && <span className={css.ceremonyCardSecondary}>{venue.address}</span>}
        </div>
      </div>
      {venue?.tel && (
        <p className={css.ceremonyTel}>
          <Phone size={10} /> {venue.tel}
        </p>
      )}
      {!dateFormatted && !venue?.name && (
        <p className={common.placeholderText}>예식 정보를 입력해주세요</p>
      )}
    </div>
  )
}
